import { Analysis, PossibleRootCauseCategory } from '.';

/**
 * The marker each Nutrients root-cause category is about.
 *
 * The section's tabs order their blocks by how concerning that marker is
 * (Priority, then Support, then Monitor), so the first supplement a
 * practitioner reads is the one for the worst marker — the same order the
 * Nutrient Insights panel's Focus list uses. See
 * nutrientCategoryOrder, which is what applies it.
 *
 * It lives here, beside calculateInsights, because that method is where a
 * category's marker is decided: adding a marker there without adding it here
 * leaves the new category unranked (it keeps its original position and sorts
 * after the ranked ones), which a test guards against.
 *
 * Markers reported in more than one unit carry one attribute per unit (copper,
 * zinc), so those categories list every variant and whichever the test actually
 * carries is the one that ranks.
 */
export const NUTRIENT_CATEGORY_ATTRIBUTES: { [category: number]: string[] } = {
    8000: ['calcium'],
    8010: ['calcium'],
    8020: ['ceruloplasmin'],
    8030: ['ceruloplasmin'],
    8040: ['copperRBCμgdL', 'copperRBCμgmL'],
    8050: ['copperRBCμgdL', 'copperRBCμgmL'],
    8060: ['copperSerummcgmL', 'copperSerumμgdL'],
    8070: ['copperSerummcgmL', 'copperSerumμgdL'],
    8072: ['copperZincRatio'],
    8074: ['copperZincRatio'],
    8076: ['percentOfFreeCopper'],
    8078: ['percentOfFreeCopper'],
    8080: ['folateRBC'],
    8090: ['folateRBC'],
    8100: ['folateSerum'],
    8110: ['folateSerum'],
    8120: ['magnesiumRBC'],
    8130: ['magnesiumRBC'],
    8140: ['magnesium'],
    8150: ['magnesium'],
    8160: ['phosphorus'],
    8170: ['phosphorus'],
    8180: ['vitaminA'],
    8190: ['vitaminA'],
    8200: ['vitaminB6'],
    8210: ['vitaminB6'],
    8220: ['vitaminB12'],
    8230: ['vitaminB12'],
    8240: ['vitaminD'],
    8250: ['vitaminD'],
    8260: ['vitaminD125'],
    8270: ['vitaminD125'],
    8280: ['zincRBCmgL', 'zincRBCμgdL'],
    8290: ['zincRBCmgL', 'zincRBCμgdL'],
    8300: ['zincSerummcgmL', 'zincSerumμgdL'],
    8310: ['zincSerummcgmL', 'zincSerumμgdL'],
    8320: ['homocysteine'],
    8330: ['homocysteine'],
};

export class NutrientsAnalysis extends Analysis {
    calculateInsights() {
        if (this.ba.calcium.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8000);
        } else if (this.ba.calcium.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8010);
        }

        if (this.ba.ceruloplasmin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8020);
        } else if (this.ba.ceruloplasmin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8030);
        }

        if (this.ba.copperRBCμgdL.isGreaterThanOptimal() || this.ba.copperRBCμgmL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8040);
        } else if (this.ba.copperRBCμgdL.isStricklyLessThanOptimal() || this.ba.copperRBCμgmL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8050);
        } 

        if (this.ba.copperSerummcgmL.isGreaterThanOptimal() || this.ba.copperSerumμgdL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8060);
        }

        if (this.ba.copperSerummcgmL.isGreaterThanOptimal() || this.ba.copperSerumμgdL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8060);
        } else if (this.ba.copperSerummcgmL.isStricklyLessThanOptimal() || this.ba.copperSerumμgdL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8070);
        }

        if (this.ba.copperZincRatio.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8072);
        } else if (this.ba.copperZincRatio.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8074);
        }

        if (this.ba.percentOfFreeCopper.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8076);
        } else if (this.ba.percentOfFreeCopper.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8078);
        }

        if (this.ba.folateRBC.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8080);
        } else if (this.ba.folateRBC.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8090);
        }

        if (this.ba.folateSerum.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8100);
        } else if (this.ba.folateSerum.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8110);
        }

        if (this.ba.magnesiumRBC.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8120);
        } else if (this.ba.magnesiumRBC.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8130);
        }

        if (this.ba.magnesium.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8140);
        } else if (this.ba.magnesium.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8150);
        }

        if (this.ba.phosphorus.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8160);
        } else if (this.ba.phosphorus.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8170);
        }

        if (this.ba.vitaminA.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8180);
        } else if (this.ba.vitaminA.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8190);
        }

        if (this.ba.vitaminB6.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8200);
        } else if (this.ba.vitaminB6.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8210);
        }

        if (this.ba.vitaminB12.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8220);
        } else if (this.ba.vitaminB12.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8230);
        }

        if (this.ba.vitaminD.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8240);
        } else if (this.ba.vitaminD.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8250);
        }

        if (this.ba.vitaminD125.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8260);
        } else if (this.ba.vitaminD125.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8270);
        }

        if (this.ba.zincRBCmgL.isGreaterThanOptimal() || this.ba.zincRBCμgdL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8280);
        } else if (this.ba.zincRBCmgL.isStricklyLessThanOptimal() || this.ba.zincRBCμgdL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8290);
        }

        if (this.ba.zincSerummcgmL.isGreaterThanOptimal() || this.ba.zincSerumμgdL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8300);
        } else if (this.ba.zincSerummcgmL.isStricklyLessThanOptimal() || this.ba.zincSerumμgdL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8310);
        }

        // Homocysteine causes read through the B-vitamin lens here (folate /
        // B12 / B6 as the cofactors that clear it). The Inflammation section
        // pushes its own homocysteine categories (13110 / 13120) for the same
        // marker, so the two sections tell their own story about one value.
        if (this.ba.homocysteine.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8320);
        } else if (this.ba.homocysteine.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_8330);
        }
    }
}